import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;
const HINT_KEY = 'diosa_auth_hint';   // lightweight, non-sensitive; only used to boot the UI into the correct state on hard-reload before /api/auth/me answers

// Read + persist the auth hint. Kept in localStorage so a hard-reload during a
// network flake at /api/auth/me doesn't visually sign the user out — the
// server-side HttpOnly cookie is still the only source of truth for authz.
const readHint = () => {
  try { const v = localStorage.getItem(HINT_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
};
const writeHint = (u) => {
  try {
    if (u && typeof u === 'object') localStorage.setItem(HINT_KEY, JSON.stringify({ id: u.id, email: u.email, name: u.name, role: u.role }));
    else localStorage.removeItem(HINT_KEY);
  } catch { /* storage disabled */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readHint());   // start from the hint so the nav renders the right state instantly
  const [loading, setLoading] = useState(true);

  // Keep the hint in sync with real user state
  useEffect(() => { writeHint(user); }, [user]);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Genuine not-logged-in — clear the hint too.
        setUser(false);
      }
      // Network errors / 5xx: keep the hint-derived state so the UI stays consistent.
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Login failed';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { name, email, password }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registration failed';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
